import React, { useState, useRef, useEffect } from 'react';
import { useT } from '../i18n/LanguageContext';

/**
 * The filter button, made to do something.
 *
 * Orders and IVR Details both rendered `<IconButton icon="filter_list" />` with NO onClick - a
 * control that looks live, invites a click and does nothing. A dead button is worse than a missing
 * one: it tells someone the feature exists and then fails silently, so they assume there is nothing
 * to filter by rather than that the button is broken.
 *
 * It filters by DATE SUBMITTED, which is the filter that was actually asked for and the one thing a
 * text search genuinely cannot express - every other field people search by is a name or an id, and
 * those go in the search box.
 *
 * The dot on the button is the important detail. A filter that is ON but scrolled out of view is
 * how someone concludes their records have vanished, so an active range is visible from the button
 * itself, and Clear is always one click away.
 */
export default function DateRangeFilter({
  dateFrom,
  dateTo,
  onChange,
  label = 'Filter by date submitted',
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const active = !!(dateFrom || dateTo);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t(label)}
        aria-expanded={open}
        title={
          active
            ? t('Filtered: {from} to {to}', { from: dateFrom || t('any'), to: dateTo || t('any') })
            : t(label)
        }
        className={`relative w-[46px] h-[46px] rounded-[10px] border flex items-center justify-center transition-colors ${
          active
            ? 'border-[#0089ff] bg-[#eff6ff] text-[#0089ff]'
            : 'border-[#e2e8f0] bg-white text-[#64748b] hover:bg-gray-50'
        }`}
      >
        <span className="material-symbols-outlined text-[20px]">filter_list</span>
        {active && (
          <span
            data-testid="filter-active-dot"
            className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-[#0089ff]"
          />
        )}
      </button>

      {open && (
        <div
          data-testid="date-range-panel"
          className="absolute right-0 mt-2 z-50 w-[260px] bg-white border border-[#e2e8f0] rounded-[12px] shadow-lg p-4"
        >
          <p className="text-[11px] font-semibold text-[#64748b] uppercase mb-2">
            {t('Date submitted')}
          </p>

          <label
            className="block text-[11px] font-medium text-[#64748b] mb-1"
            htmlFor="filter-date-from"
          >
            {t('From')}
          </label>
          <input
            id="filter-date-from"
            type="date"
            value={dateFrom || ''}
            max={dateTo || undefined}
            onChange={(e) => onChange({ dateFrom: e.target.value, dateTo })}
            className="w-full h-[38px] border border-[#e2e8f0] rounded-[8px] px-3 text-xs text-[#0f172a] mb-3 focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
          />

          <label
            className="block text-[11px] font-medium text-[#64748b] mb-1"
            htmlFor="filter-date-to"
          >
            {t('To')}
          </label>
          <input
            id="filter-date-to"
            type="date"
            value={dateTo || ''}
            min={dateFrom || undefined}
            onChange={(e) => onChange({ dateFrom, dateTo: e.target.value })}
            className="w-full h-[38px] border border-[#e2e8f0] rounded-[8px] px-3 text-xs text-[#0f172a] focus:outline-none focus:ring-2 focus:ring-[#0089ff]/20 focus:border-[#0089ff]"
          />

          <div className="flex items-center justify-between mt-4">
            <button
              type="button"
              onClick={() => onChange({ dateFrom: '', dateTo: '' })}
              disabled={!active}
              className="text-xs font-semibold text-[#64748b] hover:text-[#0f172a] disabled:opacity-40"
            >
              {t('Clear')}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="h-[32px] px-3 rounded-[8px] bg-[#0f172a] text-white text-xs font-semibold"
            >
              {t('Done')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
